import { useState, useRef, useCallback, useEffect } from 'react'
import {
  Box, CircularProgress, Divider,
  IconButton, Tooltip, Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { FS_XS, FS_XL, ICON_LG, ICON_SM, BORDER_BLACK } from '../common/theme'
import { useApp, useAppActions, useAppProject } from '../AppContext'
import { addToast } from '../services/Toasts'
import { pmGetProject } from '../services/ProjectManagerService'
import { BidQuestion } from '../comps/BidQuestion'
import StateDisplay from '../comps/StateDisplay'
import FolderChip from '../comps/FolderChip'
import { FolderIcon } from '../comps/FolderIcon'
import BidQuestionsLeftPanel from './bidmanager/BidQuestionsLeftPanel'
import BidBatchProcessModal from './modals/BidBatchProcessModal'
import BidProjectPickerModal from './modals/BidProjectPickerModal'
import TaClickZone from '../comps/TaClickZone'
import ChatPanel from './chat/ChatPanel'
import type { Question } from '../types'

// Single-question editor view. The right-hand pane is the same BidQuestion
// component the inline list view uses, so the question header / split panes
// / answer pane / debug + tweaks + advanced-edit modals are all owned by
// BidQuestion - this tab just supplies the project picker bar, the question
// list picker on the left, and the batch-process modal that the Generate
// button routes through.
export default function ResponseBuilderTab() {
  console.log('ResponseBuilderTab render')
  const { currentStyleName } = useApp()
  const { currentProject, currentQuestion } = useAppProject()
  const { setCurrentProject, setCurrentQuestion } = useAppActions()

  const [loadingProject, setLoadingProject] = useState(false)
  // Shared batch process modal - same component the BidManager uses. The
  // BidQuestion below routes its "Generate Answer" click through here
  // seeded with just the focused question (this tab has no bulk-select
  // UI on the LHS — see `bulkActions={false}` below).
  const [batchProcessOpen, setBatchProcessOpen] = useState(false)
  const [batchQuestions, setBatchQuestions] = useState<Question[]>([])
  const [projectPickerOpen, setProjectPickerOpen] = useState(false)

  // Question-list picker width + collapsed (resize divider only when expanded).
  const [pickerCollapsed, setPickerCollapsed] = useState(false)
  const [pickerWidth, setPickerWidth] = useState(280)
  const pickerRowRef = useRef<HTMLDivElement>(null)

  // State filter driven by the top-bar StateDisplay chips. Same shape
  // as BidProjectView — clicking the currently-active chip clears it.
  // The picker below only receives questions that match the filter, so
  // navigation stays scoped to the filtered set.
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined)
  const toggleStateFilter = (s: string) =>
    setStateFilter(prev => prev === s ? undefined : s)

  // Right-pane horizontal split - BidQuestion on top, ChatPanel at the
  // bottom. ChatPanel owns its own flex sizing (composer-height when
  // empty, flex:1 when a conversation is active); we only track the
  // sibling-resize state here. `chatActive` is fed by ChatPanel via
  // `onConversationChange` and gates the resize handle's visibility.
  const [chatActive, setChatActive] = useState(false)
  const [chatFlexBasis, setChatFlexBasis] = useState<number | null>(null)
  const rightPaneRef = useRef<HTMLDivElement>(null)

  const handleChatResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const pane = rightPaneRef.current
    if (!pane) return
    const startY = e.clientY
    const paneH = pane.clientHeight
    const startH = chatFlexBasis ?? Math.round(paneH / 2)
    const onMove = (ev: MouseEvent) => {
      const delta = startY - ev.clientY
      setChatFlexBasis(Math.max(120, Math.min(paneH - 200, startH + delta)))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [chatFlexBasis])

  const handlePickerResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const row = pickerRowRef.current
    if (!row) return
    const onMove = (ev: MouseEvent) => {
      const rect = row.getBoundingClientRect()
      const next = ev.clientX - rect.left
      setPickerWidth(Math.max(160, Math.min(560, next)))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [])

  const projectQuestions = currentProject?.questions ?? []
  // Filtered set fed to the LHS picker. StateDisplay itself always
  // sees the unfiltered questions so its per-state counts stay
  // stable — filtering the top bar's counts against its own filter
  // would leave the user with a single "1 selected" chip.
  const filteredQuestions = stateFilter
    ? projectQuestions.filter((q: any) => q.state === stateFilter)
    : projectQuestions

  // Central reload - pulls fresh state from the server. Refreshes
  // `currentQuestion` from the re-fetched project so BidQuestion picks up
  // the latest server values.
  const reloadProject = useCallback(async () => {
    if (!currentProject?.projectsha) return
    setLoadingProject(true)
    try {
      const result = await pmGetProject(currentProject.projectsha)
      if (result) {
        setCurrentProject(result as any)
        const refreshed = (result.questions ?? []).find((q: any) =>
          q.questionsha === currentQuestion?.questionsha
        )
        if (refreshed) setCurrentQuestion(refreshed as any)
      }
    } catch {
      addToast('error', 'Failed to reload project.')
    } finally {
      setLoadingProject(false)
    }
  }, [currentProject?.projectsha, currentQuestion?.questionsha, setCurrentProject, setCurrentQuestion, addToast])

  // Auto-fetch the full project on mount / project switch if we only have
  // the lightweight `partial` row from pmInit (no questions).
  useEffect(() => {
    if (currentProject && ((currentProject as any).partial || !currentProject.questions?.length)) {
      reloadProject()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.projectsha])

  // Open the shared batch-process modal seeded with either an explicit
  // question list (LHS panel's bulk-batch), or falling back to the
  // focused `currentQuestion` (BidQuestion's Generate button). Mirrors
  // BidProjectView's `openBatchProcess` shape.
  const openBatchProcess = useCallback((qs?: Question[]) => {
    setBatchQuestions(qs && qs.length > 0 ? qs : (currentQuestion ? [currentQuestion as any] : []))
    setBatchProcessOpen(true)
  }, [currentQuestion])

  const handleGenerateRequested = useCallback((_q: any) => {
    openBatchProcess()
  }, [openBatchProcess])

  return (
    <>
      {/* Bid picker - opens from the FolderChip at the top. */}
      <BidProjectPickerModal
        open={projectPickerOpen}
        onClose={() => setProjectPickerOpen(false)}
      />

      {/* Shared batch process modal. Seeded from `batchQuestions` —
          either the LHS panel's selection or, when the user clicked
          Generate on the focused question, just `[currentQuestion]`. */}
      {currentProject && batchQuestions.length > 0 && (
        <BidBatchProcessModal
          open={batchProcessOpen}
          projectsha={currentProject.projectsha}
          questions={batchQuestions}
          projectbideval={(currentProject as any).projectbideval}
          defaultStyleName={currentStyleName}
          onClose={() => setBatchProcessOpen(false)}
          onQuestionChange={(q: any) => setCurrentQuestion(q)}
        />
      )}

      <Box data-testid="rb_root" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', position: 'relative', }}>

        {/* ── Project / question picker bar ────────────────────────────── */}
        {currentProject && (
          <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <FolderChip
              name={currentProject.name}
              isProject
              onClick={() => setProjectPickerOpen(true)}
              tooltip="Switch bid"
              testId="rb_project_chip"
            />
            <Tooltip title="Reload Project">
              <span>
                <IconButton
                  data-testid="rb_reload_project_btn"
                  size="small"
                  onClick={reloadProject}
                  disabled={loadingProject}
                  sx={{ p: 0.25 }}
                >
                  {loadingProject
                    ? <CircularProgress size={14} thickness={5} />
                    : <RefreshIcon sx={{ fontSize: FS_XL }} />
                  }
                </IconButton>
              </span>
            </Tooltip>
            {loadingProject && projectQuestions.length === 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ opacity: 0.6, fontSize: FS_XS }}>Loading…</Typography>
                  <CircularProgress size={12} />
                </Box>
              </>
            )}
            <Box sx={{ flex: 1 }} />
            <StateDisplay
              questions={projectQuestions}
              stateFilter={stateFilter}
              onStateClick={toggleStateFilter}
              onClearFilter={() => setStateFilter(undefined)}
            />
          </Box>
        )}

        {!currentProject && (
          <TaClickZone
            testId="rb_select_project_btn"
            onClick={() => setProjectPickerOpen(true)}
            icon={
              <Box sx={{ transform: 'scale(2.2)', transformOrigin: 'center', mb: 1 }}>
                <FolderIcon isProject />
              </Box>
            }
            label="No bid selected"
            subLabel="Click to choose a bid project and start building responses."
            sx={{
              m: 1,
              gap: 1.5,
              px: 5, py: 4,
              height: '100%',
            }}
          />

        )}

        {currentProject && (
          <Box ref={pickerRowRef} sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
            {/* Left: question list picker. `bulkActions={false}` strips
                every selection affordance — this tab focuses one
                question at a time, bulk operations happen in Bid
                Manager. `onSelectQuestion` swaps the focused question
                that BidQuestion below renders. */}
            {!pickerCollapsed && (
              <Box sx={{
                flex: `0 0 ${pickerWidth}px`, minWidth: 0,
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
              }}>
                <BidQuestionsLeftPanel
                  allQuestions={filteredQuestions}
                  page={1}
                  onPage={() => { /* no pagination in this tab */ }}
                  onCollapse={() => setPickerCollapsed(true)}
                  onSelectQuestion={(q) => setCurrentQuestion(q)}
                  bulkActions={false}
                />
              </Box>
            )}

            {/* Re-expand strip when collapsed / resize handle when expanded. */}
            {pickerCollapsed ? (
              <Box
                sx={{
                  width: 18, flexShrink: 0,
                  borderRight: 1, borderColor: 'divider',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  pt: 0.5, bgcolor: 'action.hover',
                }}
              >
                <Tooltip title="Show questions panel" placement="right">
                  <IconButton
                    data-testid="rb_questionpicker_expand_btn"
                    size="small"
                    onClick={() => setPickerCollapsed(false)}
                    sx={{ p: 0.25 }}
                  >
                    <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box
                data-testid="rb_questionpicker_resize"
                onMouseDown={handlePickerResizeStart}
                sx={{
                  width: 7, flexShrink: 0, cursor: 'col-resize',
                  bgcolor: 'transparent',
                  borderLeft: 1, borderRight: 1, borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
              </Box>
            )}

            {/* Right: BidQuestion stacked above a KbChatPanel. The bid
                question expands to fill the remaining height; the chat
                panel keeps a user-resizable fixed pixel height. */}
            <Box
              ref={rightPaneRef}
              sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {currentQuestion && currentProject && (
                  <BidQuestion
                    question={currentQuestion}
                    projectsha={currentProject.projectsha}
                    onGenerate={handleGenerateRequested}
                    fillHeight
                  />
                )}
                {!currentQuestion && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="caption" sx={{ opacity: 0.45, fontSize: FS_XS }}>
                      Please select or add questions
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Horizontal drag handle - only meaningful once a chat
                  conversation is active; until then ChatPanel collapses to
                  the composer's natural height and there's nothing to
                  resize. */}
              {chatActive && (
                <Box
                  data-testid="rb_chat_resize"
                  onMouseDown={handleChatResizeStart}
                  sx={{
                    height: 7, flexShrink: 0,
                    cursor: 'row-resize',
                    borderTop: 1, borderBottom: 1, borderColor: 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <MoreHorizIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
                </Box>
              )}

              {/* Chat panel - owns its own flex sizing. When a conversation
                  is active and the user has dragged the resize handle, we
                  pin it to `chatFlexBasis` pixels; otherwise it auto-sizes
                  (composer-height when empty, half the pane when first
                  conversation starts). */}
              <Box sx={{
                display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
                ...(chatActive && chatFlexBasis != null
                  ? { flex: `0 0 ${chatFlexBasis}px` }
                  : null),
              }}>
                <ChatPanel
                  currentQuestion={currentQuestion}
                  hideCloseButton
                  onConversationChange={(hasMessages) => {
                    if (hasMessages && !chatActive) {
                      const paneH = rightPaneRef.current?.clientHeight ?? window.innerHeight
                      setChatFlexBasis(Math.round(paneH / 2))
                    }
                    setChatActive(hasMessages)
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box >
    </>
  )
}

